import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import * as api from './api';
import type {
  AssignTestInput,
  AttemptAnswers,
  AttemptListParams,
  GradeAttemptInput,
  QuestionWriteInput,
  TestListParams,
  TestWriteInput,
} from './types';

/** Query-key factories — every assessments key derives from these. */
export const testKeys = {
  all: ['tests'] as const,
  lists: () => [...testKeys.all, 'list'] as const,
  list: (params: TestListParams) => [...testKeys.lists(), params] as const,
  details: () => [...testKeys.all, 'detail'] as const,
  detail: (publicId: string) => [...testKeys.details(), publicId] as const,
};

export const questionKeys = {
  all: ['test-questions'] as const,
  list: (testId: string) => [...questionKeys.all, 'list', testId] as const,
};

export const attemptKeys = {
  all: ['attempts'] as const,
  lists: () => [...attemptKeys.all, 'list'] as const,
  list: (params: AttemptListParams) => [...attemptKeys.lists(), params] as const,
  details: () => [...attemptKeys.all, 'detail'] as const,
  detail: (publicId: string) => [...attemptKeys.details(), publicId] as const,
};

// ---------------------------------------------------------------------------
// Tests
// ---------------------------------------------------------------------------

export function useTests(params: TestListParams = {}, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: testKeys.list(params),
    queryFn: () => api.getTests(params),
    placeholderData: keepPreviousData,
    enabled: options.enabled,
  });
}

export function useTest(publicId: string) {
  return useQuery({
    queryKey: testKeys.detail(publicId),
    queryFn: () => api.getTest(publicId),
    enabled: Boolean(publicId),
  });
}

export function usePupilTest(publicId: string, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: testKeys.detail(publicId),
    queryFn: () => api.getPupilTest(publicId),
    enabled: (options.enabled ?? true) && Boolean(publicId),
  });
}

export function useCreateTest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TestWriteInput) => api.createTest(input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: testKeys.lists() }),
  });
}

export function useUpdateTest(publicId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TestWriteInput) => api.updateTest(publicId, input),
    onSuccess: detail => {
      qc.setQueryData(testKeys.detail(publicId), detail);
      void qc.invalidateQueries({ queryKey: testKeys.lists() });
    },
  });
}

export function useArchiveTest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => api.archiveTest(publicId),
    onSuccess: (_data, publicId) => {
      void qc.invalidateQueries({ queryKey: testKeys.lists() });
      void qc.invalidateQueries({ queryKey: testKeys.detail(publicId) });
    },
  });
}

export function usePublishTest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => api.publishTest(publicId),
    onSuccess: detail => {
      qc.setQueryData(testKeys.detail(detail.public_id), detail);
      void qc.invalidateQueries({ queryKey: testKeys.lists() });
    },
  });
}

export function useUnpublishTest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => api.unpublishTest(publicId),
    onSuccess: detail => {
      qc.setQueryData(testKeys.detail(detail.public_id), detail);
      void qc.invalidateQueries({ queryKey: testKeys.lists() });
    },
  });
}

export function useDuplicateTest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => api.duplicateTest(publicId),
    onSuccess: copy => {
      qc.setQueryData(testKeys.detail(copy.public_id), copy);
      void qc.invalidateQueries({ queryKey: testKeys.lists() });
    },
  });
}

export function useAssignTest(publicId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AssignTestInput) => api.assignTest(publicId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: testKeys.detail(publicId) });
      void qc.invalidateQueries({ queryKey: testKeys.lists() });
    },
  });
}

export function useUnassignTest(publicId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (schoolClass: string) => api.unassignTest(publicId, schoolClass),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: testKeys.detail(publicId) });
      void qc.invalidateQueries({ queryKey: testKeys.lists() });
    },
  });
}

// ---------------------------------------------------------------------------
// Questions
// ---------------------------------------------------------------------------

export function useQuestions(testId: string, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: questionKeys.list(testId),
    queryFn: () => api.getQuestions(testId),
    enabled: (options.enabled ?? true) && Boolean(testId),
  });
}

/** Every question write invalidates the test detail too — `questions_count`
 *  and the frozen-questions state live there. */
function useQuestionMutation<TVars, TData>(
  testId: string,
  mutationFn: (vars: TVars) => Promise<TData>
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: questionKeys.list(testId) });
      void qc.invalidateQueries({ queryKey: testKeys.detail(testId) });
      void qc.invalidateQueries({ queryKey: testKeys.lists() });
    },
  });
}

export function useCreateQuestion(testId: string) {
  return useQuestionMutation(testId, (input: QuestionWriteInput) => api.createQuestion(input));
}

export function useUpdateQuestion(testId: string) {
  return useQuestionMutation(
    testId,
    ({ publicId, input }: { publicId: string; input: Partial<QuestionWriteInput> }) =>
      api.updateQuestion(publicId, input)
  );
}

export function useDeleteQuestion(testId: string) {
  return useQuestionMutation(testId, (publicId: string) => api.deleteQuestion(publicId));
}

export function useReorderQuestion(testId: string) {
  return useQuestionMutation(testId, ({ publicId, order }: { publicId: string; order: number }) =>
    api.reorderQuestion(publicId, order)
  );
}

// ---------------------------------------------------------------------------
// Attempts
// ---------------------------------------------------------------------------

export function useAttempts(params: AttemptListParams = {}, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: attemptKeys.list(params),
    queryFn: () => api.getAttempts(params),
    placeholderData: keepPreviousData,
    enabled: options.enabled,
  });
}

export function usePupilAttempt(publicId: string) {
  return useQuery({
    queryKey: attemptKeys.detail(publicId),
    queryFn: () => api.getPupilAttempt(publicId),
    enabled: Boolean(publicId),
    // The taking screen owns local answer state; refetching mid-attempt would
    // fight the autosave.
    refetchOnWindowFocus: false,
  });
}

export function useStaffAttempt(publicId: string) {
  return useQuery({
    queryKey: attemptKeys.detail(publicId),
    queryFn: () => api.getStaffAttempt(publicId),
    enabled: Boolean(publicId),
  });
}

export function useStartAttempt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (testId: string) => api.startAttempt(testId),
    onSuccess: attempt => {
      qc.setQueryData(attemptKeys.detail(attempt.public_id), attempt);
      void qc.invalidateQueries({ queryKey: attemptKeys.lists() });
    },
  });
}

/** Autosave. Deliberately does NOT invalidate: the taking screen is the source
 *  of truth for in-flight answers, and a refetch mid-typing would clobber them. */
export function useSaveAnswers(publicId: string) {
  return useMutation({
    mutationFn: (answers: AttemptAnswers) => api.saveAttemptAnswers(publicId, answers),
  });
}

export function useSubmitAttempt(publicId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.submitAttempt(publicId),
    onSuccess: attempt => {
      qc.setQueryData(attemptKeys.detail(publicId), attempt);
      void qc.invalidateQueries({ queryKey: attemptKeys.lists() });
    },
  });
}

export function useGradeAttempt(publicId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: GradeAttemptInput) => api.gradeAttempt(publicId, input),
    onSuccess: attempt => {
      qc.setQueryData(attemptKeys.detail(publicId), attempt);
      void qc.invalidateQueries({ queryKey: attemptKeys.lists() });
    },
  });
}
